import React from "react";
import { RiStackLine } from "react-icons/ri";
import {
  ACCENT,
  INK,
  LINE,
  OVERLAY_SHADOW,
  SURFACE,
  neutralText,
} from "./layerStyle";

/**
 * What the trigger says about the canvas, for its aria-label and tooltip:
 * 「圖層：地圖開啟，平面圖關閉，已隱藏 基站、機具」, or just 「圖層」 when
 * everything is at its default.
 */
export const layerSummary = ({ mapOn, planOff, hiddenLabels }) => {
  const parts = [
    mapOn && "地圖開啟",
    planOff && "平面圖關閉",
    hiddenLabels.length > 0 && `已隱藏 ${hiddenLabels.join("、")}`,
  ].filter(Boolean);
  return parts.length ? `圖層：${parts.join("，")}` : "圖層";
};

const chip = {
  display: "inline-flex",
  alignItems: "center",
  height: 18,
  padding: "0 6px",
  borderRadius: 9,
  fontSize: 12,
  lineHeight: "18px",
  fontWeight: 500,
  color: SURFACE,
  whiteSpace: "nowrap",
};

/**
 * The 圖層 button on the canvas rail. A <div role="button">, not a <button>:
 * css/button.css (and every theme file) restyles each <button> with skew,
 * uppercase, forced margins, min-width and a drop shadow, so the trigger
 * carries its whole look inline and never meets those rules. State stays
 * readable from across the room: a blue 「地圖」 chip while the map is on,
 * a dark 「N 隱藏」 chip while overlays are hidden.
 */
const LayerTrigger = React.forwardRef(
  ({ open, mapOn, hiddenCount, summary, className, ...rest }, ref) => (
    <div
      ref={ref}
      // Popover and Tooltip add their own open class; keep ours beside it.
      className={`iot3-layer-trigger ${className || ""}`.trim()}
      data-testid="layer-trigger"
      role="button"
      tabIndex={0}
      aria-label={summary}
      aria-haspopup="dialog"
      aria-expanded={open}
      {...rest}
      style={{
        ...neutralText,
        "--iot3-layer-accent": ACCENT,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        height: 28,
        margin: 0,
        padding: "0 10px",
        boxSizing: "border-box",
        borderRadius: 4,
        border: `1px solid ${open ? ACCENT : LINE}`,
        backgroundColor: SURFACE,
        boxShadow: OVERLAY_SHADOW,
        transform: "none",
        color: INK,
        fontWeight: 500,
        lineHeight: "26px",
        whiteSpace: "nowrap",
        cursor: "pointer",
        userSelect: "none",
        pointerEvents: "auto",
        transition: "border-color 150ms ease-out",
      }}
    >
      <RiStackLine
        aria-hidden="true"
        style={{ fontSize: 15, color: open ? ACCENT : INK }}
      />
      圖層
      {mapOn && (
        <span
          data-testid="layer-map-indicator"
          style={{ ...chip, backgroundColor: ACCENT }}
        >
          地圖
        </span>
      )}
      {hiddenCount > 0 && (
        <span
          data-testid="layer-hidden-badge"
          style={{ ...chip, backgroundColor: "#434343" }}
        >
          {hiddenCount} 隱藏
        </span>
      )}
    </div>
  ),
);

LayerTrigger.displayName = "LayerTrigger";

export default LayerTrigger;
